import { ref } from 'vue';
import ConversationApi from '../api/inbox/conversation';

export function useRecommendationManager(store) {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Checks if recommendations exist for a conversation in the store or DB
   * @param {number} conversationId - The conversation ID
   * @param {string} [lastMessageId] - The ID of the last message to check against
   * @returns {Promise<{exists: boolean, iqRecommendation: string|null, eqRecommendation: Array|null, messageId: string|null}>}
   * @param {boolean} exists: true if recommendations exist in the store or DB
   * @param {string} messageId: the message ID for which recommendations were generated
   */
  const checkExistingRecommendations = async (
    conversationId,
    lastMessageId = null,
    isContact = false
  ) => {
    // First check the store
    const iqRecommendation = store.getters.getIqRecommendation(conversationId);
    const eqRecommendation = store.getters.getEqRecommendation(conversationId);
    const sentimentScores = store.getters.getSentimentScores(conversationId);
    const storedMessageId =
      store.getters.getRecommendationMessageId(conversationId);

    if (storedMessageId) {
      if (storedMessageId === lastMessageId) {
        return {
          exists: true,
          iqRecommendation,
          eqRecommendation,
          sentimentScores,
          messageId: storedMessageId,
        };
      } else {
        return {
          exists: false,
          iqRecommendation: null,
          eqRecommendation: [],
          sentimentScores: [],
          messageId: null,
        };
      }
    } else {
      try {
        isLoading.value = true;
        const { data: recommendationData } =
        await ConversationApi.getRecommendations(conversationId);
        const { data, success } = recommendationData;
        if (success && data) {
          // if the last message(contact type) id is the same as the stored message id in db
          // or the last message is not a contact message, 
          // then update the store
          if (data.eqiq_msg_id === lastMessageId || !isContact) {
            // Update store
            if (data.iq_recommendation) {
              store.dispatch('setIqRecommendation', {
                conversationId,
                iqRecommendation: data.iq_recommendation,
              });
            }
          
            if (data.eq_recommendation) {
              store.dispatch('setEqRecommendation', {
                conversationId,
                eqRecommendation: data.eq_recommendation,
              });
            }

            if (data.eqiq_msg_id) {
              store.dispatch('setRecommendationMessageId', {
                conversationId,
                messageId: data.eqiq_msg_id,
              });
            }

            if (data.sentiment_scores) {
              store.dispatch('setSentimentScores', {
                conversationId,
                newSentimentScore: data.sentiment_scores,
                replace: true,
              });
            }
                    
            return {
              exists: true,
              iqRecommendation: data.iq_recommendation,
              eqRecommendation: data.eq_recommendation,
              sentimentScores: data.sentiment_scores,
              messageId: data.eqiq_msg_id,
            };
          } else {
            return {
              exists: false,
              iqRecommendation: null,
              eqRecommendation: [],
              sentimentScores: [],
              messageId: null,
            };
          }
        }
      } catch (err) {
        error.value = err;
      } finally {
        isLoading.value = false;
      }
    }
    // fallback case :: if all fails, return get a new recommendation from /chat-messages
    return {
      exists: false,
      iqRecommendation: null,
      eqRecommendation: [],
      sentimentScores: [],
      messageId: null,
    };
  };

  /**
   * Saves recommendations to the database
   * @param {number} conversationId - The conversation ID
   * @param {string} iqRecommendation - The IQ recommendation
   * @param {Array} eqRecommendation - The EQ recommendations
   * @param {string} messageId - The message ID for which recommendations were generated
   * @param {Object} sentimentScore - The sentiment scores for the conversation
   */
  const saveRecommendationsToDatabase = async (
    conversationId,
    iqRecommendation,
    eqRecommendation,
    messageId = null,
    sentimentScore = null
  ) => {
    if (!conversationId) return;

    try {
      await ConversationApi.saveRecommendations({
        conversationId,
        iqRecommendation,
        eqRecommendation,
        messageId,
        sentimentScores: sentimentScore,
      });
    } catch (err) {
      console.error('Error saving recommendations to DB:', err);
    }
  };

  return {
    isLoading,
    error,
    checkExistingRecommendations,
    saveRecommendationsToDatabase,
  };
}
